import React from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { pad } from "../timeUtils";

/**
 * Confirms the appointment move triggered by a drag-and-drop.
 * `dropConfirm` shape:
 *   {
 *     appt,
 *     staff_id,            // drop-target column
 *     minute,               // drop-target minute
 *     role: "primary"|"assist",
 *     dragged_from_staff_id, // origin column (assist drags only)
 *   } | null
 *
 * Iter-350 · Role-Aware Drag Split.
 *   • Primary tile → dialog offers a staff swap + time move.
 *   • Assist  tile → dialog spells out that the Primary practitioner is
 *     LOCKED; a sideways move swaps the assistant, a vertical move
 *     reschedules the whole appointment.
 */
export default function MoveConfirmDialog({ dropConfirm, staffList, onConfirm, onClose }) {
  const targetName = dropConfirm
    ? (staffList.find((s) => s.id === dropConfirm.staff_id)?.full_name || "another staff")
    : "";
  const minuteLabel = dropConfirm
    ? `${pad(Math.floor(dropConfirm.minute / 60))}:${pad(dropConfirm.minute % 60)}`
    : "";
  const isAssist = dropConfirm?.role === "assist";
  const isSideways = isAssist && dropConfirm.staff_id !== dropConfirm.dragged_from_staff_id;
  const primaryName = dropConfirm
    ? (staffList.find((s) => s.id === dropConfirm.appt.staff_id)?.full_name || "primary practitioner")
    : "";
  return (
    <AlertDialog open={!!dropConfirm} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="move-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="move-confirm-title">
            {isAssist ? (isSideways ? "Reassign assistant?" : "Move this appointment?") : "Move this appointment?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {dropConfirm && (
              <>
                <span className="font-medium">{dropConfirm.appt.customer_name || "Walk-in"}</span> — {dropConfirm.appt.service_name}
                <br />
                {isAssist && isSideways ? (
                  <>
                    Swap assistant to <span className="font-medium">{targetName}</span> at <span className="font-medium">{minuteLabel}</span>.
                    <br />
                    <span className="text-[11px] text-muted-foreground" data-testid="move-confirm-primary-locked">
                      Primary practitioner (<span className="font-medium">{primaryName}</span>) stays locked.
                    </span>
                  </>
                ) : (
                  <>
                    Move to <span className="font-medium">{targetName}</span> at <span className="font-medium">{minuteLabel}</span>?
                    {isAssist && (
                      <>
                        <br />
                        <span className="text-[11px] text-muted-foreground" data-testid="move-confirm-primary-locked">
                          Time change applies to the whole team — Primary (<span className="font-medium">{primaryName}</span>) stays locked.
                        </span>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="move-confirm-no">No</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="move-confirm-yes">
            {isAssist && isSideways ? "Yes, reassign" : "Yes, move"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
